import { useTranslate } from '@i18n';
import { Inline, type InlineProps, u, Box, Button } from '@ui';
import { FC } from 'react';

type InlineComponentProps = Pick<InlineProps, 'gap' | 'className' | 'p'>;

export type WizardFooterProps = InlineComponentProps & {
    isLastStep: boolean;
    canGoNext: boolean;
    canGoBack: boolean;
    canFinish?: boolean;
    isFinishDisabled: boolean;
    isFinishLoading: boolean;
    onNext?: () => void;
    onBack?: () => void;
    onFinish?: () => void;
    onCancel?: () => void;
    nextLabel?: string;
    finishLabel?: string;
};

export const WizardFooter: FC<WizardFooterProps> = ({
    isLastStep,
    canGoNext,
    canGoBack,
    canFinish = false,
    isFinishDisabled,
    isFinishLoading,
    onNext,
    onBack,
    onFinish,
    onCancel,
    nextLabel,
    finishLabel,
    gap = u(2),
    ...rest
}) => {
    const t = useTranslate();

    return (
        <Inline
            justifyContent="space-between"
            alignItems="center"
            flexWrap={{ xs: 'wrap', md: 'nowrap' }}
            p={u(2)}
            gap={gap}
            {...rest}
        >
            <Box display="flex" alignItems="center" gap={u(2)}>
                {onCancel && (
                    <Button variant="plain" size="medium" onClick={onCancel}>
                        {t('generic_ui.close')}
                    </Button>
                )}

                <Button variant="secondary" size="medium" onClick={onBack} isDisabled={!canGoBack || !onBack}>
                    {t('generic_ui.back')}
                </Button>
            </Box>

            <Box display="flex" alignItems="center" gap={u(2)}>
                {!isLastStep && (
                    <Button variant="primary" size="medium" onClick={onNext} isDisabled={!canGoNext || !onNext}>
                        {nextLabel ?? t('generic_ui.next')}
                    </Button>
                )}

                {(isLastStep || canFinish) && (
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={onFinish}
                        isDisabled={isFinishDisabled || !onFinish}
                        isLoading={isFinishLoading}
                    >
                        {finishLabel ?? t('generic_ui.done')}
                    </Button>
                )}
            </Box>
        </Inline>
    );
};
